import { useState, useEffect, useCallback } from "react";
import { servicePlansService } from "../services/servicePlansService";
import type { UserSubscription } from "../../../shared/types/servicePlans";

export const useSubscription = () => {
  const [subscription, setSubscription] = useState<UserSubscription | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadSubscription = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await servicePlansService.getUserSubscription();
      // Suppress debug logs for subscription data (only log if needed for debugging)
      setSubscription(data);
    } catch (err: any) {
      // Don't set error for 404/403 (new user without subscription)
      // 403 = user exists but hasn't signed up for a plan yet (expected for new users)
      if (err.status !== 404 && err.status !== 403) {
        setError(err);
      } else {
        // Clear subscription for 404/403 (user needs to sign up)
        setSubscription(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  // No plan-based limits -- everything is unlimited, token balance is the only gate
  const getRemainingQuotas = (_currentCounts: {
    resumes: number;
    jobApplications: number;
  }) => {
    return {
      resumes: null,
      jobApplications: null,
    };
  };

  // All features are always available -- token balance is checked on the backend
  const isFeatureAvailable = (
    _feature:
      | "intelligentSearch"
      | "intelligentCompanySearch"
      | "resumeJobMatch",
  ) => {
    return true;
  };

  // No plan-based quotas -- always return null (unlimited)
  const getFeatureRemainingQuota = (
    _feature:
      | "intelligentSearch"
      | "intelligentCompanySearch"
      | "resumeJobMatch",
    _currentUsage: number = 0,
  ) => {
    return null;
  };

  // No plan-based limits -- never exceeded, token balance is the only gate
  const isLimitExceeded = (
    _type: "resumes" | "jobApplications",
    _currentCount: number,
  ) => {
    return false;
  };

  return {
    subscription,
    isLoading,
    error,
    reload: loadSubscription,
    getRemainingQuotas,
    isFeatureAvailable,
    getFeatureRemainingQuota,
    isLimitExceeded,
    // Legacy alias for backward compatibility during migration
    isFeatureEnabled: isFeatureAvailable,
  };
};
